import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';
import { BoxV2 as Box } from 'portal-commons';
import { useAuthContext } from '@/contexts';
import { getCspProfile } from '@/apis/user';
import { Loader } from '@/shared_elements';
import { RootReducer } from '@/store/types';
import { StatusMessageBar, DisplayType } from '../components/StatusMessageBar';

const CspVerificationStatus: React.FC = () => {
  const { profile, setProfile } = useAuthContext();
  const history = useHistory();
  const [loading, setLoading] = useState(true);

  const cspData = useSelector(
    (state: RootReducer) => state.shareReducer.cspData
  );

  useEffect(() => {
    document.title = 'The Campaign Registry - Approval Status';
  }, []);

  // Ensure profile is loaded
  useEffect(() => {
    if (!profile) {
      getCspProfile()
        .then((data) => {
          if (data) setProfile(data);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [profile, setProfile]);

  const rawStatus = profile?.status?.toLowerCase() || '';

  // Redirection logic
  useEffect(() => {
    if (rawStatus === 'active') {
      history.push('/');
    }
  }, [rawStatus, history]);

  // Mapping backend status to display categories
  const displayCategory = useMemo(() => {
    if (rawStatus === 'reject') return 'rejected';
    if (rawStatus === 'suspend') return 'suspended';
    if (['review', 'queue', 'appeal'].includes(rawStatus)) return 'pending';
    return rawStatus;
  }, [rawStatus]);

  // Convert internal category to StatusMessageBar DisplayType
  const displayType = useMemo((): DisplayType => {
    const mapping: Record<string, DisplayType> = {
      pending: 'info',
      suspended: 'warning',
      rejected: 'error',
    };
    return mapping[displayCategory] || 'info';
  }, [displayCategory]);

  // Fetch dynamic content from Redux
  const statusData = useMemo(() => {
    if (!cspData || !cspData.length || !displayCategory) return null;
    const lookupKey = `csp-verification-status#${displayCategory}`;
    return cspData.find((item) => item.lookup_key === lookupKey);
  }, [cspData, displayCategory]);

  const title = statusData?.title || 'CSP Verification Status';
  const content = statusData?.text
    ? parse(statusData.text)
    : 'Your application status is being updated.';

  if (loading || !statusData) return <Loader />;

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backgroundColor: '#fff',
      }}
    >
      <StatusMessageBar
        displayType={displayType}
        title={title}
        content={content}
      />
    </Box>
  );
};

export default CspVerificationStatus;
